import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/styles';
import { Bar, Pie } from 'react-chartjs-2';
import moment from 'moment';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  colors,
} from '@material-ui/core';
import Page from '../../components/Page';
import { NetworkContext } from '../../context/NetworkContext';
import Header from '../DashboardAnalytics/components/Header';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  item: {
    padding: theme.spacing(3),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      '&:not(:last-of-type)': {
        borderRight: `1px solid ${theme.palette.divider}`
      }
    }
  },
  chart: {
    height: 320
  },
  contentCard: {
    height: '100%'
  },
  select: {
    minWidth: 280
  }
}));

const PIE_COLORS = [
  colors.indigo[500],
  colors.teal[500],
  colors.orange[500],
  colors.purple[400],
  colors.blue[500],
  colors.red[400],
  colors.green[500],
  colors.grey[500],
];

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    xAxes: [{
      gridLines: { display: false, drawBorder: false },
      ticks: { maxTicksLimit: 10 }
    }],
    yAxes: [{
      ticks: { beginAtZero: true, precision: 0 }
    }]
  }
};

export const SurveyAnalytics = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { sendNetworkRequest } = React.useContext(NetworkContext);
  const [startDate, setStartDate] = useState(moment().subtract(29, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [surveyId, setSurveyId] = useState('');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  async function fetchAnalytics(start, end, selectedId) {
    setLoading(true);
    const body = {
      start_date: moment(start).format('YYYY-MM-DD'),
      end_date: moment(end).format('YYYY-MM-DD'),
    };
    if (selectedId) {
      body.survey_id = selectedId;
    }
    const result = await sendNetworkRequest('/admin/getsurveyanalytics', {}, body, false);
    setPayload(result);
    if (result?.selected_survey_id && !selectedId) {
      setSurveyId(String(result.selected_survey_id));
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchAnalytics(startDate, endDate, '');
  }, []);

  function handleRangeChange(start, end) {
    setStartDate(start);
    setEndDate(end);
    fetchAnalytics(start, end, surveyId);
  }

  function handleSurveyChange(event) {
    const nextId = event.target.value;
    setSurveyId(nextId);
    fetchAnalytics(startDate, endDate, nextId);
  }

  const totals = payload?.totals || {};
  const responsesByDay = payload?.responses_by_day || [];
  const surveys = payload?.surveys || [];
  const options = payload?.options || [];
  const optionTotal = options.reduce((sum, opt) => sum + (opt.count || 0), 0);

  const responseChart = {
    labels: responsesByDay.map(row => moment(row.day).format('DD MMM')),
    datasets: [{
      label: 'Responses',
      backgroundColor: theme.palette.primary.main,
      data: responsesByDay.map(row => row.count),
    }]
  };

  const optionChart = {
    labels: options.map(opt => opt.option || `Option ${opt.id}`),
    datasets: [{
      data: options.map(opt => opt.count || 0),
      backgroundColor: options.map((_, index) => PIE_COLORS[index % PIE_COLORS.length]),
      borderWidth: 0,
    }]
  };

  return (
    <Page
      className={classes.root}
      title="Survey Analytics"
    >
      <Header
        title="Survey Analytics"
        startDate={startDate}
        endDate={endDate}
        onRangeChange={handleRangeChange}
      />
      {loading && <LinearProgress />}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <Grid container>
              <Grid className={classes.item} item md={3} sm={6} xs={12}>
                <Typography gutterBottom variant="overline">Surveys</Typography>
                <Typography variant="h3">{formatNumber(totals.surveys)}</Typography>
              </Grid>
              <Grid className={classes.item} item md={3} sm={6} xs={12}>
                <Typography gutterBottom variant="overline">Responses in range</Typography>
                <Typography variant="h3">{formatNumber(totals.responses_in_range)}</Typography>
              </Grid>
              <Grid className={classes.item} item md={3} sm={6} xs={12}>
                <Typography gutterBottom variant="overline">Unique respondents</Typography>
                <Typography variant="h3">{formatNumber(totals.unique_respondents)}</Typography>
              </Grid>
              <Grid className={classes.item} item md={3} sm={6} xs={12}>
                <Typography gutterBottom variant="overline">Announced</Typography>
                <Typography variant="h3">{formatNumber(totals.announced)}</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="overline">All-time responses</Typography>
              <Typography variant="h4">{formatNumber(totals.responses)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="overline">Active surveys</Typography>
              <Typography variant="h4">{formatNumber(totals.active_surveys)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="overline">Comments in range</Typography>
              <Typography variant="h4">{formatNumber(totals.comments)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={8} xs={12}>
          <Card className={classes.contentCard}>
            <CardHeader title="Responses" subheader="By day in the selected range" />
            <Divider />
            <CardContent>
              <div className={classes.chart}>
                <Bar data={responseChart} options={barOptions} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={4} xs={12}>
          <Card className={classes.contentCard}>
            <CardHeader title="Choice breakdown" />
            <Divider />
            <CardContent>
              {surveys.length > 0 && (
                <TextField
                  className={classes.select}
                  fullWidth
                  select
                  size="small"
                  value={surveyId}
                  onChange={handleSurveyChange}
                  variant="outlined"
                  style={{ marginBottom: 16 }}
                >
                  {surveys.map(survey => (
                    <MenuItem key={survey.id} value={String(survey.id)}>
                      {survey.question}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <div className={classes.chart}>
                {options.length ? <Pie data={optionChart} options={{ maintainAspectRatio: false, legend: { position: 'bottom' } }} /> : (
                  <Typography color="textSecondary">No choice data for this survey in the selected range</Typography>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={6} xs={12}>
          <Card className={classes.contentCard}>
            <CardHeader
              title="Selected survey options"
              action={payload?.selected_survey_id ? (
                <Button
                  color="primary"
                  component={RouterLink}
                  size="small"
                  to={`/surveyresults/${payload.selected_survey_id}`}
                  variant="outlined"
                >
                  Results
                </Button>
              ) : null}
            />
            <Divider />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Option</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {options.length ? options.map(opt => (
                  <TableRow key={opt.id}>
                    <TableCell>{opt.option}</TableCell>
                    <TableCell align="right">{formatNumber(opt.count)}</TableCell>
                    <TableCell align="right">
                      {optionTotal ? `${Math.round((opt.count / optionTotal) * 100)}%` : '0%'}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={3}>No options</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item md={6} xs={12}>
          <Card className={classes.contentCard}>
            <CardHeader title="Surveys" />
            <Divider />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Responses</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {surveys.length ? surveys.map(survey => (
                  <TableRow key={survey.id}>
                    <TableCell>{survey.question}</TableCell>
                    <TableCell>{survey.answer_type === 2 ? 'Free text' : 'Choice'}</TableCell>
                    <TableCell align="right">{formatNumber(survey.response_count)}</TableCell>
                    <TableCell align="right">
                      <Button
                        color="primary"
                        component={RouterLink}
                        size="small"
                        to={`/surveyresults/${survey.id}`}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4}>No surveys</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </Grid>
      </Grid>
    </Page>
  );
};

export default SurveyAnalytics;
